import React, { createContext, useContext } from 'react'

import { LOCAL_STORAGE_KEYS } from 'config/Vars'

import useLocalStorage from 'modules/core/lib/useLocalStorage'

export type ScheduleVisibilityOptions = {
  showAvatar: boolean
  showRole: boolean
  showPlanningHours: boolean
  showAgreementHours: boolean
}

const DEFAULT_VISIBILITY_OPTIONS: ScheduleVisibilityOptions = {
  showAvatar: true,
  showRole: true,
  showPlanningHours: true,
  showAgreementHours: true,
}

type ContextType = {
  visibilityOptions: ScheduleVisibilityOptions
  updateVisibilityOptions: (key: keyof ScheduleVisibilityOptions, value: boolean) => void
}

const ScheduleVisibilityOptionsContext = createContext<ContextType | undefined>(undefined)

export const ScheduleVisibilityOptionsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [visibilityOptions, setVisibilityOptions] = useLocalStorage<ScheduleVisibilityOptions>(
    LOCAL_STORAGE_KEYS.projectsScheduleVisibilityOptions,
    DEFAULT_VISIBILITY_OPTIONS
  )

  const updateVisibilityOptions = (key: keyof ScheduleVisibilityOptions, value: boolean) => {
    setVisibilityOptions((prev) => ({
      ...(prev ?? DEFAULT_VISIBILITY_OPTIONS),
      [key]: value,
    }))
  }

  return (
    <ScheduleVisibilityOptionsContext.Provider
      value={{
        visibilityOptions: visibilityOptions ?? DEFAULT_VISIBILITY_OPTIONS,
        updateVisibilityOptions,
      }}
    >
      {children}
    </ScheduleVisibilityOptionsContext.Provider>
  )
}

export const useScheduleVisibilityOptions = () => {
  const context = useContext(ScheduleVisibilityOptionsContext)
  if (!context) {
    throw new Error(
      'useScheduleVisibilityOptions must be used within a ScheduleVisibilityOptionsProvider'
    )
  }
  return context
}
